import { useMemo, useState } from 'react';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/cn';
import { CheckCircle2, AlertTriangle, FileText, Lock, X, Copy } from 'lucide-react';
import { useCode99Store } from './code99-store';
import { computeCompliance } from './compliance';
import { buildCode99Narrative } from './narrative';

/**
 * ClosureModal — surfaces the compliance checklist and the
 * specialized Code 99 narrative before a resuscitation session can
 * be closed. Two-stage:
 *
 *   Stage 1 — Compliance Checklist
 *     Lists every required element (R1–R7 in compliance.ts). Each
 *     row shows done / missing with a hint. Missing items are shown
 *     as a clean to-do, not screamed at. Close is gated here.
 *
 *   Stage 2 — Narrative Preview
 *     Shows the auto-generated draft + protocol compliance footer.
 *     User can edit the text before approval. Approval stamps
 *     narrative_approved_at + narrative_approved_by on the session,
 *     sets closed_at, and writes the compliance snapshot.
 */

interface Props {
  onDismiss: () => void;
  onClosed: () => void;
}

export function ClosureModal({ onDismiss, onClosed }: Props) {
  const { user } = useAuthStore();
  const { session, events, updateSession } = useCode99Store();
  const [stage, setStage] = useState<'checklist' | 'narrative'>('checklist');
  const [editedNarrative, setEditedNarrative] = useState<string | null>(null);

  const report = useMemo(
    () => computeCompliance(session, events),
    [session, events],
  );

  const bundle = useMemo(() => {
    if (!session) return null;
    return buildCode99Narrative(session, events);
  }, [session, events]);

  if (!session || !bundle) return null;

  async function finalize() {
    const narrativeText = editedNarrative ?? `${bundle!.draft}\n\n${bundle!.complianceFooter}`;
    // Persist: stamp closed + narrative approved + compliance snapshot
    await updateSession({
      closed_at: new Date().toISOString(),
      closed_by: user?.id ?? null,
      narrative_generated_at: new Date().toISOString(),
      narrative_approved_at: new Date().toISOString(),
      narrative_approved_by: user?.id ?? null,
      compliance_check: {
        closed_at: new Date().toISOString(),
        results: report.results,
      },
    });
    // Stash the final narrative text on the session's compliance_check
    // (separate column would be cleaner but we're not adding schema mid-session —
    //  parent narrative tab can read this back from compliance_check.narrative)
    await updateSession({
      compliance_check: {
        closed_at: new Date().toISOString(),
        results: report.results,
        narrative: narrativeText,
      },
    });
    onClosed();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-red-700 bg-slate-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-red-700 bg-red-950/60 px-3 py-2">
          <div className="flex items-center gap-2">
            <Lock size={14} className="text-red-300" />
            <span className="text-sm font-black uppercase tracking-widest text-red-200">
              Close Code 99 · {stage === 'checklist' ? 'Compliance Check' : 'Narrative Review'}
            </span>
          </div>
          <button onClick={onDismiss} className="h-8 w-8 flex items-center justify-center text-red-200 hover:bg-red-900/40">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {stage === 'checklist' ? (
            <ChecklistBody report={report} />
          ) : (
            <NarrativeBody
              draft={(editedNarrative ?? `${bundle.draft}\n\n${bundle.complianceFooter}`)}
              onChange={setEditedNarrative}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-700 bg-slate-900 px-3 py-2">
          {stage === 'checklist' ? (
            <>
              <div className={cn(
                'text-[11px] font-mono uppercase tracking-widest',
                report.canClose ? 'text-emerald-300' : 'text-amber-300',
              )}>
                {report.canClose
                  ? 'All required elements documented'
                  : `${report.blockersFailing} required item${report.blockersFailing === 1 ? '' : 's'} pending`}
              </div>
              <button
                onClick={() => setStage('narrative')}
                disabled={!report.canClose}
                className={cn(
                  'btn-primary gap-1 text-sm',
                  !report.canClose && 'opacity-40 pointer-events-none',
                )}
              >
                <FileText size={14} />
                Review Narrative
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStage('checklist')}
                className="btn-secondary text-xs"
              >
                Back
              </button>
              <button onClick={finalize} className="btn-primary gap-1 text-sm">
                <Lock size={14} />
                Approve &amp; Close Code 99
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Stage 1 — Checklist
// ============================================================
function ChecklistBody({ report }: { report: ReturnType<typeof computeCompliance> }) {
  return (
    <div className="divide-y divide-slate-800">
      {report.results.map((r, i) => (
        <div key={i} className={cn(
          'flex items-start gap-2 px-3 py-2',
          !r.ok && r.blocker && 'bg-amber-950/20',
          !r.ok && !r.blocker && 'bg-slate-900/40',
        )}>
          {r.ok
            ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
            : r.blocker
              ? <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
              : <AlertTriangle size={16} className="text-slate-500 shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn(
                'text-sm font-semibold',
                r.ok ? 'text-slate-300' :
                r.blocker ? 'text-amber-100' : 'text-slate-200',
              )}>
                {r.label}
              </span>
              {!r.blocker && (
                <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
                  best-practice
                </span>
              )}
            </div>
            {!r.ok && r.detail && (
              <p className="mt-0.5 text-[11px] text-slate-400 leading-snug">{r.detail}</p>
            )}
          </div>
        </div>
      ))}
      <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
        Close is gated on the required items. Best-practice items are shown for completeness but won't block closure.
      </div>
    </div>
  );
}

// ============================================================
// Stage 2 — Narrative
// ============================================================
function NarrativeBody({
  draft, onChange,
}: {
  draft: string;
  onChange: (v: string | null) => void;
}) {
  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Auto-generated resuscitation narrative
        </div>
        <button
          onClick={() => navigator.clipboard?.writeText(draft)}
          className="text-[10px] text-blue-300 hover:text-blue-200 inline-flex items-center gap-1"
        >
          <Copy size={11} /> Copy
        </button>
      </div>
      <textarea
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        rows={18}
        className="input-field font-mono text-[12px] leading-snug whitespace-pre"
      />
      <p className="text-[10px] text-slate-500 leading-snug">
        Every line traces to a timestamped event in the Code 99 log. Edit freely before approval —
        edits are preserved and distinguishable from the auto-generated text in the version history.
        On approve, this text is stamped with your identity and the session closes.
      </p>
    </div>
  );
}
